import { useCallback } from "react";
import { useWsSubscription } from "hooks/useWsSubscription";
import { useLiveNotifications } from "pages/media/analytics/hooks/useLiveNotifications";
import { useLiveViewersStore } from "store/liveViewersStore";
import { NOTIFICATIONS_EVENT_TYPE, type WSEvent } from "store/websocketStore";

/**
 * Listens to the organization's live-analytics feed and writes it into the
 * live viewers store and the notifications bell.
 */
export function useOrganizationLiveEvents(
  organizationId: string | undefined
): void {
  const setProjectViewers = useLiveViewersStore(
    (state) => state.setProjectViewers
  );
  const {
    handlePeak,
    handleMilestone,
    handleTrending,
    handleChatbotQuestion,
    handleQuizSubmitted,
  } = useLiveNotifications(organizationId);

  const handler = useCallback(
    (event: WSEvent) => {
      const projectId = event.project_id;
      if (!organizationId || !projectId) return;

      switch (event.event) {
        case NOTIFICATIONS_EVENT_TYPE.ProjectViewersUpdate:
          if (typeof event.viewer_count === "number") {
            setProjectViewers(organizationId, projectId, event.viewer_count);
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.NewMaxConcurrentViewers:
          if (typeof event.max_concurrent === "number") {
            handlePeak(projectId, event.max_concurrent);
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.ViewMilestoneReached:
          if (typeof event.total_views === "number") {
            handleMilestone(projectId, event.total_views);
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.ProjectTrending:
          handleTrending(projectId);
          break;
        case NOTIFICATIONS_EVENT_TYPE.ChatbotMessageAnswered:
          handleChatbotQuestion(projectId);
          break;
        case NOTIFICATIONS_EVENT_TYPE.QuizSubmitted:
          handleQuizSubmitted(projectId, event.score ?? undefined);
          break;
        default:
          break;
      }
    },
    [
      organizationId,
      setProjectViewers,
      handlePeak,
      handleMilestone,
      handleTrending,
      handleChatbotQuestion,
      handleQuizSubmitted,
    ]
  );

  useWsSubscription({ type: "organization", id: organizationId }, handler);
}
